import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Admin-configurable public contact details, so the platform's support/legal
 * contact points are country-agnostic (set once in Admin → Settings) instead of
 * hardcoded per page. Consumed by the footer, Contact, Safety, and the legal
 * pages. Reads from app_settings; falls back to neutral corporate defaults when
 * a key has not been set yet.
 */
export interface ContactSettings {
  support_email: string;
  support_phone: string;
  privacy_email: string;
  legal_email: string;
  safety_email: string;
}

/**
 * The Kanfy mailbox, the one place the website and app name it. Every contact
 * point falls back to it; the Edge emails use the same address from
 * supabase/functions/_shared/supportInbox.ts (src/test/supportInbox.test.ts
 * keeps the two equal).
 */
export const KANFY_SUPPORT_EMAIL = 'support@kanfy.io';

// Corporate defaults — intentionally not country-specific. The admin overrides
// these in Settings; phone is left blank so nothing invents a number.
export const DEFAULT_CONTACT: ContactSettings = {
  support_email: KANFY_SUPPORT_EMAIL,
  support_phone: '',
  privacy_email: KANFY_SUPPORT_EMAIL,
  legal_email: KANFY_SUPPORT_EMAIL,
  safety_email: KANFY_SUPPORT_EMAIL,
};

export const CONTACT_SETTING_KEYS: Record<keyof ContactSettings, string> = {
  support_email: 'contact_support_email',
  support_phone: 'contact_support_phone',
  privacy_email: 'contact_privacy_email',
  legal_email: 'contact_legal_email',
  safety_email: 'contact_safety_email',
};

export function useContactSettings() {
  const query = useQuery({
    queryKey: ['contact-settings'],
    queryFn: async (): Promise<ContactSettings> => {
      const { data, error } = await supabase
        .from('app_settings')
        .select('setting_key, setting_value')
        .in('setting_key', Object.values(CONTACT_SETTING_KEYS));

      if (error) {
        console.error('Error fetching contact settings:', error);
        return DEFAULT_CONTACT;
      }

      const bySettingKey = new Map((data ?? []).map((r) => [r.setting_key, r.setting_value]));
      const resolved = { ...DEFAULT_CONTACT };
      (Object.keys(CONTACT_SETTING_KEYS) as (keyof ContactSettings)[]).forEach((field) => {
        const value = bySettingKey.get(CONTACT_SETTING_KEYS[field]);
        // A blank stored value falls back to the default (except phone, where
        // blank is a legitimate "no public number" choice).
        if (value != null && (value.trim() !== '' || field === 'support_phone')) {
          resolved[field] = value;
        }
      });
      return resolved;
    },
    staleTime: 1000 * 60 * 5,
    retry: 1,
    meta: { errorMessage: 'Failed to load contact settings' },
  });

  return { contact: query.data ?? DEFAULT_CONTACT, ...query };
}
